

































































































import { Component, Mixins, Emit, Prop } from 'vue-property-decorator';
import SFormElement from '@/components/UI/Form/SFormElement.vue';
import SFilePreviews from './SFilePreviews.vue';
import imageCompression from 'browser-image-compression';

type CompressingOptions = {
  maxSizeMB: number;
  maxWidthOrHeight: number;
  useWebWorker: boolean;
}

@Component({
  i18n: {
    messages: {
      en: {
        Upload: {
          uploading: 'Uploading...',
          compressing: 'Compressing...',
          fileCount: '{count} file | {count} files',
          dragHereMultiple: 'Drag the files here to upload or click to select the files',
          dragHere: 'Drag the file here to upload or click to select the file',
        },
      },
      nb: {
        Upload: {
          uploading: 'Laster opp...',
          compressing: 'Compressing...',
          fileCount: '{count} fil | {count} filer',
          dragHereMultiple: 'Dra filer hit for å laste opp, eller klikk her å velge filer',
          dragHere: 'Dra filen hit for å laste opp, eller klikk her å velge filen',
        },
      },
    },
  },
  components: {
    SFilePreviews,
  },
})
export default class SUpload extends Mixins(SFormElement) {
  @Prop({ type: Boolean, default: false }) public fullWidth!: boolean;
  @Prop({ type: Boolean, default: false }) public isLoading!: boolean;
  @Prop({ type: Boolean, default: false }) public multiple!: boolean;
  @Prop({ type: String, default: 'medium' }) public size!: 'small' | 'medium' | 'large';
  @Prop({ type: Array, default: () => [] }) private allowedContentTypes!: string[];
  @Prop(Boolean) private disabled?: boolean;
  @Prop({ type: Boolean, default: true }) public showFileCount!: boolean;
  @Prop({ type: Boolean, default: false }) public useImageCompression!: boolean;
  @Prop({ type: Object, default: () => (
    {
      maxSizeMB: 1,
      maxWidthOrHeight: 1680,
      useWebWorker: true,
    }
  ), 
  }) public imageCompressionOptions!: CompressingOptions;

  private showContentTypeError = false;
  public dragover = false;
  public files: File[] | null = null;

  public iconSizes = {
    small: '',
    medium: 'fa-2x',
    large: 'fa-3x',
  };

  get listeners() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { input, ...listeners } = this.$listeners;
    return listeners;
  }

  @Emit('input')
  public removeFile(file: File) {
    if (!this.files) return null;

    this.files.splice(this.files.indexOf(file), 1);

    return this.multiple ? this.files : this.files[0];
  }

  private isCompressing = false;

  public async handleInput(event: { target: HTMLInputElement }) {
    const inputFiles = event.target.files
      ? (this.multiple ? Array.from(event.target.files) : [ event.target.files[0] ])
      : null;

    if (inputFiles) {
      this.files = this.useImageCompression
        ? await this.compressImages(inputFiles)
        : inputFiles;
    } else {
      this.files = null;
    }
    this.validateContentType();
    this.$emit('input', this.multiple ? this.files : this.files && this.files[0]);
  }

  @Emit('input')
  public onDrop(event: DragEvent) {
    this.dragover = false;
    this.files = event.dataTransfer
      ? (this.multiple ? Array.from(event.dataTransfer.files) : [ event.dataTransfer.files[0] ])
      : null;
    this.validateContentType();
    return this.multiple ? this.files : this.files && this.files[0];
  }

  private validateContentType() {
    if (this.files === null || this.allowedContentTypes.length === 0) return;
    this.showContentTypeError = false;
    this.files.forEach(file => {
      if (!this.allowedContentTypes.includes(file.type)) {
        this.showContentTypeError = true;
        this.files = null;
      }
    });
  }

  private async compressImages(files: File[]): Promise<File[]> {
    const { maxSizeMB } = this.imageCompressionOptions;
    this.isCompressing = true;

    try {
      return await Promise.all(
        files.map(file =>
          file.type.startsWith("image/") && maxSizeMB && file.size / 1024 / 1024 >= maxSizeMB
            ? imageCompression(file, this.imageCompressionOptions)
            : Promise.resolve(file),
        ),
      );
    } catch (error) {
      return files;
    } finally {
      this.isCompressing = false;
    }
  }

  private get allowedFileExtensions() {
    let allowed = ``;
    this.allowedContentTypes.forEach(filetype => {
      filetype = filetype.split('/')[1];
      allowed += `.${filetype}, `;
    });

    return allowed.substring(0, allowed.length - 2);
    // return '.png, .jpg, .jpeg';
  }
}
